import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, viewChildren } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { ContentItemTypeEnum } from '@examdojo/models/content-item';
import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import { QuestionImageFormViewerComponent } from '@examdojo/question-image';
import { MultiLineTextComponent } from '@examdojo/ui/multi-line-text';
import isEqual from 'lodash/isEqual';
import { distinctUntilChanged, merge, of, switchMap } from 'rxjs';
import {
  ContentItemFormArray,
  ContentItemFormGroup,
  createContentItemFormControl,
} from './question-content-item.model';
import { QuestionTableFormViewerComponent } from './table-editor/question-table-form-viewer.component';

@Component({
  selector: 'dojo-content-item-editor',
  template: `
    <div class="bg-neutral-0 header flex items-center gap-2">
      <span class="text-md text-primary font-semibold">{{ title() }} Content</span>
      @if (canAddRemoveText() || canAddRemoveImage()) {
        <dojo-button [matMenuTriggerFor]="menu" [size]="'xs'" [fill]="'outline'" [customColor]="'sky'">
          Add
        </dojo-button>
      }
    </div>

    @if (contentItemsFormArray().controls.length === 0) {
      <div class="text-neutral-300 text-center border pt-3 pb-3 mt-3 rounded-lg text-md">No Content Here</div>
    }

    <div
      class="text-primary 1pl-2 flex h-full flex-col gap-2"
      cdkDropList
      #mainDropList="cdkDropList"
      [cdkDropListData]="contentItemsFormArray()"
      (cdkDropListDropped)="drop($event)"
      [cdkDropListOrientation]="'vertical'"
    >
      @for (item of contentItemsFormArray().controls; track $index) {
        @let i = $index;
        <div
          class="flex items-start gap-1 py-1"
          cdkDrag
          [cdkDragData]="item"
          [cdkDragDisabled]="!canReorder()"
        >
          @if (canReorder()) {
            <dojo-icon-button
            [hidden]="i === 0 && contentItemsFormArray().controls.length === 1"
            [icon]="'grip-vertical'" [iconSize]="14" cdkDragHandle class="cursor-move-button mt-2 self-start" />
          }
          @switch (item.value.type) {
            @case (ContentItemTypeEnum.Text) {
              <dojo-multi-line-text
                #textarea
                class="h-full w-full"
                [formCtrl]="$any(item.controls.content)"
              />
            }
            @case (ContentItemTypeEnum.Image) {
              <div class="flex w-full flex-col gap-2 rounded-xl border border-neutral-200 p-2">
                <div class="relative flex w-full gap-2 min-h-8">
                  <dojo-question-image-form-viewer [formCtrl]="$any(item)" />

                  <dojo-button
                    (click)="editItem.emit({ item, contentItemIndex: $index })"
                    [hidden]="contentItemEditingOptions().allowImageEditing.length === 0"
                    class="absolute right-[-5px] bottom-0"
                    [size]="'xs'"
                    [fill]="'outline'"
                  >
                    <dojo-icon [icon]="'pencil'" class="mr-2" />
                    <span>Edit image</span>
                  </dojo-button>
                </div>
              </div>
            }
            @case (ContentItemTypeEnum.Table) {
              <div class="flex w-full flex-col gap-2 rounded-xl border border-neutral-200 p-2 overflow-auto max-h-[350px]">
                <div class="relative flex w-full gap-2 min-h-8">
                  <dojo-question-table-form-viewer [formCtrl]="$any(item)" />

                  <dojo-button
                    (click)="editItem.emit({ item, contentItemIndex: $index })"
                    [hidden]="contentItemEditingOptions().allowImageEditing.length === 0 && !contentItemEditingOptions().allowTextEditing"
                    class="absolute right-[-5px] bottom-0"
                    [size]="'xs'"
                    [fill]="'outline'"
                  >
                    <dojo-icon [icon]="'pencil'" class="mr-2" />
                    <span>Edit table</span>
                  </dojo-button>
                </div>
              </div>
            }
          }
          @if (canAddRemoveText() || canAddRemoveImage()) {
            <dojo-icon-button [icon]="'trash-can'" (click)="removeItem($index)" class="mt-[5px] self-start" />
          }
        </div>
      }

      <mat-menu #menu="matMenu" class="custom-mat-menu">
        @if (canAddRemoveText()) {
          <button mat-menu-item (click)="addItem(ContentItemType.Text)">Text</button>
          <button mat-menu-item (click)="addItem(ContentItemType.Table)">Table</button>
        }
        @if (canAddRemoveImage()) {
          <button mat-menu-item (click)="addItem(ContentItemType.Image)">Image</button>
        }
      </mat-menu>
    </div>
  `,
  host: {
    class: 'w-full',
  },
  imports: [
    MatIconModule,
    MatMenuModule,
    CdkDropList,
    CdkDrag,
    MultiLineTextComponent,
    QuestionImageFormViewerComponent,
    QuestionTableFormViewerComponent,
    ButtonComponent,
    IconButtonComponent,
    CdkDragHandle,
    ReactiveFormsModule,
    IconComponent,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentItemEditorComponent {
  constructor() {
    // Update disabled state of image content controls when editing options change
    effect(() => {
      const editingOptions = this.contentItemEditingOptions();
      const formArray = this.contentItemsFormArray();

      if (!formArray) {
        return;
      }

      formArray.controls.forEach((control) => {
        if (control.value.type === ContentItemTypeEnum.Image) {
          const contentControl = control.controls.content;
          if (editingOptions.allowImageEditing && contentControl.disabled) {
            contentControl.enable();
          } else if (!editingOptions.allowImageEditing && contentControl.enabled) {
            contentControl.disable();
          }
        }
      });
    });
  }

  readonly contentItemsFormArray = input.required<ContentItemFormArray>();

  readonly canAddRemoveText = input.required<boolean>();
  readonly canAddRemoveImage = input.required<boolean>();
  readonly canReorder = input.required<boolean>();
  readonly supportsFillInTheBlanks = input<boolean>(false);

  readonly editingOptions = input.required<QuestionEditingOptions>();

  // Allow overriding text editing for specific contexts (e.g., markscheme editing)
  readonly allowTextEditingOverride = input<boolean | undefined>(undefined);

  readonly contentItemEditingOptions = computed(() => ({
    allowTextEditing: this.allowTextEditingOverride() ?? this.editingOptions().allowQuestionEditing,
    allowImageEditing: this.editingOptions().allowImageEditing,
    allowFillInTheBlanks: this.supportsFillInTheBlanks(),
    hideImageAndTableMetadata: this.editingOptions().hideImageAndTableMetadata,
  }));

  readonly title = input<string>();

  readonly editItem = output<{ item: ContentItemFormGroup; contentItemIndex: number }>();

  readonly multiLineTextComponents = viewChildren<MultiLineTextComponent>('textarea');
  readonly ContentItemTypeEnum = ContentItemTypeEnum;

  readonly ContentItemType = ContentItemTypeEnum;

  readonly contentItems$ = toObservable(this.contentItemsFormArray).pipe(
    switchMap((formArray) => merge(formArray?.valueChanges ?? of([]), of(this.contentItemsFormArray()?.value))),
    distinctUntilChanged((x, y) => isEqual(x, y)),
  );

  readonly state = connectState({
    contentItems: this.contentItems$,
  });

  addItem(type: ContentItemTypeEnum) {
    switch (type) {
      case ContentItemTypeEnum.Text:
        this.contentItemsFormArray()?.push(
          createContentItemFormControl(
            {
              type,
              content: '',
            },
            this.contentItemEditingOptions(),
          ),
        );
        break;
      case ContentItemTypeEnum.Image:
        this.contentItemsFormArray()?.push(
          createContentItemFormControl(
            {
              type,
              content: {
                url: null,
                original_url: null,
                code: null,
                generation_completed: false,
              },
              description: '',
              title: '',
              not_to_scale: undefined,
              style: {},
              container_width: '100%',
            },
            this.contentItemEditingOptions(),
          ),
        );
        break;
      case ContentItemTypeEnum.Table:
        this.contentItemsFormArray()?.push(
          createContentItemFormControl(
            {
              type,
              content: '',
              title: '',
              style: {},
              container_width: '100%',
            },
            this.contentItemEditingOptions(),
          ),
        );
        break;
    }

    setTimeout(() => {
      this.multiLineTextComponents().at(-1)?.focusElement();
    }, 100);

    this.contentItemsFormArray().markAsDirty();
  }

  removeItem(index: number) {
    this.contentItemsFormArray().removeAt(index);
    this.contentItemsFormArray().markAsDirty();
  }

  drop(event: CdkDragDrop<ContentItemFormArray>) {
    if (!event.container.data) {
      return;
    }

    moveItemInArray(event.container.data.controls, event.previousIndex, event.currentIndex);
    this.contentItemsFormArray().markAsDirty();
    this.contentItemsFormArray().updateValueAndValidity();
  }
}
